import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';
import {
  Button,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
  Pagination,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetEmailTemplates } from 'src/hooks/email-templates/get-email-templates.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useCreateEmailTemplate } from 'src/hooks/email-templates/create-email-template.hook';
import PermissionCheck from 'src/components/permission-check';

import { EmailTemplateForm } from './components/email-template-form';
import { EmailTemplateCard } from './components/email-template-card';
import { HeaderContainer, MainContainer } from './email-templates.styles';
import { EmailTemplateEdit } from './components/email-template-edit';

const NEW_TEMPLATE = 'new';

const EmailTemplates = () => {
  const { t } = useTranslation('settings');
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null);

  const currentCompanyId = useGetCurrentCompanyId();
  const { emailTemplates, paginationConfig, loading } = useGetEmailTemplates(currentCompanyId);

  const handleClose = () => {
    setCurrentTemplateId(null);
  };

  const createEmailTemplate = useCreateEmailTemplate({ onSuccess: handleClose });

  const onEmailCreateFinish = (values: any) => {
    createEmailTemplate({ variables: { payload: { ...values, companyId: currentCompanyId } } });
  };

  const isNewTemplate = currentTemplateId === NEW_TEMPLATE;

  if (loading) {
    return <Loader />;
  }

  return (
    <MainContainer>
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t(`emailTemplate.labels.${isNewTemplate ? 'createNewEmailTemplate' : 'emailTemplates'}`)}
        </Typography>
        {!currentTemplateId && (
          <PermissionCheck
            permission={PLATFORM_PERMISSIONS.emailTemplates}
            action={PERMISSION_ACTIONS.create}
          >
            <Button
              type="primary"
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.white}
              onClick={() => setCurrentTemplateId(NEW_TEMPLATE)}
            >
              {t('emailTemplate.buttons.createNewTemplate')}
            </Button>
          </PermissionCheck>
        )}
      </HeaderContainer>
      {currentTemplateId ? (
        <Fragment>
          {isNewTemplate ? (
            <EmailTemplateForm onClose={handleClose} onFinish={onEmailCreateFinish} />
          ) : (
            <EmailTemplateEdit templateId={currentTemplateId} onClose={handleClose} />
          )}
        </Fragment>
      ) : (
        <div>
          {emailTemplates.map((template: any) => (
            <EmailTemplateCard
              openEdit={setCurrentTemplateId}
              template={template}
              key={template._id}
              onChangePagination={() => {
                if (emailTemplates.length <= 1 && paginationConfig.current !== 1) {
                  paginationConfig.onChange(paginationConfig.current - 1);
                }
              }}
            />
          ))}
        </div>
      )}
      <Pagination
        total={paginationConfig.total}
        pageSize={paginationConfig.pageSize}
        current={paginationConfig.current}
        onChange={paginationConfig.onChange}
        showTotal={total => (
          <Typography>{`${t('common:labels.totalEntries')} ${total}`}</Typography>
        )}
      />
    </MainContainer>
  );
};

export default EmailTemplates;
